import { useEffect, useRef, useCallback, useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { 
  MetadataFieldConfig, 
  updateJsonWithFormFields, 
  extractFormValuesFromJson,
  areValuesDifferent,
  autoDiscoverMetadataFields,
  METADATA_FIELD_CONFIGS 
} from '@/utils/metadataSynchronization';
import { SolutionMetadata } from '@/types/solution';

/**
 * Tracks which field type was last modified
 */
type LastModifiedSource = 'form' | 'json' | 'none';

/**
 * Options for the intelligent metadata synchronization hook
 */
export interface UseIntelligentMetadataSyncOptions {
  /** The react-hook-form instance */
  form: UseFormReturn<any>;
  /** The name of the JSON content field in the form */
  jsonContentFieldName?: string;
  /** Optional manual field configurations (if provided, auto-discovery is skipped) */
  fieldConfigs?: MetadataFieldConfig[];
  /** Sample metadata to help with auto-discovery */
  sampleMetadata?: Partial<SolutionMetadata>;
  /** Whether to enable auto-discovery of metadata fields */
  enableAutoDiscovery?: boolean;
}

/**
 * Hook for intelligent bidirectional metadata synchronization that tracks
 * which field was last modified and syncs accordingly
 */
export function useIntelligentMetadataSync({
  form,
  jsonContentFieldName = 'jsonContent',
  fieldConfigs,
  sampleMetadata,
  enableAutoDiscovery = true
}: UseIntelligentMetadataSyncOptions) {
  
  const lastModifiedSource = useRef<LastModifiedSource>('none');
  const previousFormValues = useRef<Record<string, any>>({});
  const previousJsonContent = useRef<string>('');
  
  // Memoize field configurations to prevent repeated auto-discovery of metadata fields
  const memoizedFieldConfigs = useMemo((): MetadataFieldConfig[] => {
    if (fieldConfigs && fieldConfigs.length > 0) {
      return fieldConfigs;
    }
    
    if (enableAutoDiscovery) {
      // Auto-discover metadata fields from form and JSON content
      return autoDiscoverMetadataFields(form, sampleMetadata);
    }
    
    // Fallback to common fields if auto-discovery is disabled
    return [
      METADATA_FIELD_CONFIGS.name,
      METADATA_FIELD_CONFIGS.shortDescription,
      METADATA_FIELD_CONFIGS.tags,
      METADATA_FIELD_CONFIGS.imageUrl
    ];
  }, [
    fieldConfigs, 
    enableAutoDiscovery, 
    sampleMetadata,
    // Only re-run auto-discovery when JSON content actually changes
    form.watch(jsonContentFieldName),
    // Re-run when form structure changes (new fields added)
    Object.keys(form.getValues()).length
  ]);
  
  // Auto-discover or use provided field configurations
  const getFieldConfigs = useCallback((): MetadataFieldConfig[] => {
    return memoizedFieldConfigs;
  }, [memoizedFieldConfigs]);
  
  // Track form field changes
  useEffect(() => {
    const configs = getFieldConfigs();
    const watchedFields = configs.map(config => config.formFieldName);
    const currentFormValues = form.getValues();
    
    // Check if any form field has changed
    const hasFormFieldChanged = watchedFields.some(fieldName => {
      const currentValue = currentFormValues[fieldName];
      const previousValue = previousFormValues.current[fieldName];
      return areValuesDifferent(currentValue, previousValue);
    });
    
    if (hasFormFieldChanged) {
      lastModifiedSource.current = 'form';
    }
    
    // Update previous values
    previousFormValues.current = { ...currentFormValues };
  }, [
    // Watch all discovered form fields dynamically
    ...getFieldConfigs().map(config => form.watch(config.formFieldName)),
    form,
    getFieldConfigs
  ]);
  
  // Track JSON content changes
  useEffect(() => {
    const currentJsonContent = form.watch(jsonContentFieldName);
    
    if (currentJsonContent !== previousJsonContent.current && 
        currentJsonContent && currentJsonContent.trim() !== '') {
      lastModifiedSource.current = 'json';
    }
    
    previousJsonContent.current = currentJsonContent;
  }, [form.watch(jsonContentFieldName), form, jsonContentFieldName]);
  
  /**
   * Synchronizes metadata based on the last modified source
   * Call this when user switches tabs or submits the form
   */
  const syncMetadata = useCallback(() => {
    const configs = getFieldConfigs();
    const source = lastModifiedSource.current;
    
    if (source === 'form') {
      // Form was last modified - sync form → JSON
      const formValues = form.getValues();
      const jsonContent = form.getValues(jsonContentFieldName);
      
      const hasFormValues = configs.some(config => {
        const value = formValues[config.formFieldName];
        return value !== undefined && value !== '' && 
               (Array.isArray(value) ? value.length > 0 : true);
      });
      
      if (jsonContent && hasFormValues) {
        const updatedJsonContent = updateJsonWithFormFields(
          jsonContent, 
          formValues, 
          configs
        );
        
        if (updatedJsonContent && updatedJsonContent !== jsonContent) {
          form.setValue(jsonContentFieldName, updatedJsonContent);
        }
      }
    } else if (source === 'json') {
      // JSON was last modified - sync JSON → form
      const jsonContent = form.getValues(jsonContentFieldName);
      
      if (jsonContent && jsonContent.trim() !== '') {
        const extractedValues = extractFormValuesFromJson(jsonContent, configs);
        
        if (extractedValues) {
          let hasChanges = false;
          
          configs.forEach(config => {
            const extractedValue = extractedValues[config.formFieldName];
            const currentValue = form.getValues(config.formFieldName);
            
            if (extractedValue !== undefined && areValuesDifferent(extractedValue, currentValue)) {
              form.setValue(config.formFieldName, extractedValue);
              hasChanges = true;
            }
          });
        }
      }
    }
    
    // Reset the tracking after sync
    lastModifiedSource.current = 'none';
  }, [form, jsonContentFieldName, getFieldConfigs]);
  
  /**
   * Gets the current last modified source (for debugging/UI purposes)
   */
  const getLastModifiedSource = useCallback(() => {
    return lastModifiedSource.current;
  }, []);
  
  /**
   * Gets the currently discovered/configured field mappings
   */
  const getFieldMappings = useCallback(() => {
    return getFieldConfigs();
  }, [getFieldConfigs]);
  
  return {
    syncMetadata,
    getLastModifiedSource,
    getFieldMappings
  };
}

/**
 * Options for the metadata synchronization hook
 */
export interface UseMetadataSyncOptions {
  /** The react-hook-form instance */
  form: UseFormReturn<any>;
  /** The name of the JSON content field in the form */
  jsonContentFieldName?: string;
  /** Optional manual field configurations (if provided, auto-discovery is skipped) */
  fieldConfigs?: MetadataFieldConfig[];
  /** Whether to enable forward sync (form → JSON) */
  enableForwardSync?: boolean;
  /** Whether to enable reverse sync (JSON → form) */
  enableReverseSync?: boolean;
  /** Sample metadata to help with auto-discovery */
  sampleMetadata?: Partial<SolutionMetadata>;
  /** Whether to enable auto-discovery of metadata fields */
  enableAutoDiscovery?: boolean;
}

/**
 * Custom hook for bidirectional metadata synchronization between form fields and JSON content
 * Now with auto-discovery capabilities for future-proof field mapping
 * 
 * @example
 * ```typescript
 * // Auto-discovery mode (recommended)
 * useMetadataSync({
 *   form,
 *   enableForwardSync: true,
 *   enableReverseSync: true
 * });
 * 
 * // Manual configuration mode
 * useMetadataSync({
 *   form,
 *   fieldConfigs: [
 *     { metadataKey: 'name', formFieldName: 'solutionTitle' },
 *     { metadataKey: 'customField', formFieldName: 'solutionCustomField' }
 *   ],
 *   enableAutoDiscovery: false
 * });
 * ```
 */
export function useMetadataSync({
  form,
  jsonContentFieldName = 'jsonContent',
  fieldConfigs,
  enableForwardSync = true,
  enableReverseSync = true,
  sampleMetadata,
  enableAutoDiscovery = true
}: UseMetadataSyncOptions) {

  // Memoize field configurations to prevent repeated auto-discovery
  const memoizedFieldConfigs = useMemo((): MetadataFieldConfig[] => {
    if (fieldConfigs && fieldConfigs.length > 0) {
      return fieldConfigs;
    }
    
    if (enableAutoDiscovery) {
      return autoDiscoverMetadataFields(form, sampleMetadata);
    }
    
    // Fallback to common fields if auto-discovery is disabled
    return [
      METADATA_FIELD_CONFIGS.name,
      METADATA_FIELD_CONFIGS.shortDescription,
      METADATA_FIELD_CONFIGS.tags,
      METADATA_FIELD_CONFIGS.imageUrl
    ];
  }, [
    fieldConfigs, 
    enableAutoDiscovery, 
    sampleMetadata,
    // Only re-run auto-discovery when JSON content actually changes
    form.watch(jsonContentFieldName),
    // Re-run when form structure changes (new fields added)
    Object.keys(form.getValues()).length
  ]);

  // Auto-discover or use provided field configurations
  const getFieldConfigs = useCallback((): MetadataFieldConfig[] => {
    return memoizedFieldConfigs;
  }, [memoizedFieldConfigs]);

  // Memoize watched fields to prevent recalculation
  const watchedFields = useMemo(() => {
    return memoizedFieldConfigs.map(config => config.formFieldName);
  }, [memoizedFieldConfigs]);

  // Memoize form values extraction to reduce object creation
  const currentFormValues = useMemo(() => {
    return form.getValues();
  }, [
    // Watch only the fields we care about to minimize re-renders
    ...watchedFields.map(fieldName => form.watch(fieldName))
  ]);

  // Forward synchronization: Form fields → JSON content
  useEffect(() => {
    if (!enableForwardSync) return;

    const configs = getFieldConfigs();
    const jsonContent = form.getValues(jsonContentFieldName);
    
    // Check if any watched fields have values
    const hasFormValues = watchedFields.some(fieldName => {
      const value = currentFormValues[fieldName];
      return value !== undefined && value !== '' && 
             (Array.isArray(value) ? value.length > 0 : true);
    });
    
    if (jsonContent && hasFormValues) {
      const updatedJsonContent = updateJsonWithFormFields(
        jsonContent, 
        currentFormValues, 
        configs
      );
      
      if (updatedJsonContent && updatedJsonContent !== jsonContent) {
        form.setValue(jsonContentFieldName, updatedJsonContent);
      }
    }
  }, [
    // Use memoized values instead of recalculating
    currentFormValues,
    watchedFields,
    form.getValues(jsonContentFieldName),
    enableForwardSync,
    getFieldConfigs,
    form,
    jsonContentFieldName
  ]);

  // Memoize JSON content extraction
  const currentJsonContent = useMemo(() => {
    return form.watch(jsonContentFieldName);
  }, [form.watch(jsonContentFieldName)]);

  // Reverse synchronization: JSON content → Form fields
  useEffect(() => {
    if (!enableReverseSync) return;
    
    if (currentJsonContent && currentJsonContent.trim() !== '') {
      const configs = getFieldConfigs();
      const extractedValues = extractFormValuesFromJson(currentJsonContent, configs);
      
      if (extractedValues) {
        let hasChanges = false;
        
        // Check each field for changes and update if necessary
        configs.forEach(config => {
          const extractedValue = extractedValues[config.formFieldName];
          const currentValue = form.getValues(config.formFieldName);
          
          if (extractedValue !== undefined && areValuesDifferent(extractedValue, currentValue)) {
            form.setValue(config.formFieldName, extractedValue);
            hasChanges = true;
          }
        });
      }
    }
  }, [
    currentJsonContent,
    enableReverseSync,
    getFieldConfigs,
    form
  ]);
}

/**
 * Convenience hooks for common use cases with auto-discovery
 */

/**
 * Hook for synchronizing only tags (most common use case)
 */
export function useTagsSync(form: UseFormReturn<any>, options?: Partial<UseMetadataSyncOptions>) {
  return useMetadataSync({
    form,
    fieldConfigs: [METADATA_FIELD_CONFIGS.tags],
    enableAutoDiscovery: false, // Use specific config for tags
    ...options
  });
}

/**
 * Hook for synchronizing title and description only
 */
export function useTitleDescriptionSync(form: UseFormReturn<any>, options?: Partial<UseMetadataSyncOptions>) {
  return useMetadataSync({
    form,
    fieldConfigs: [
      METADATA_FIELD_CONFIGS.name,
      METADATA_FIELD_CONFIGS.shortDescription
    ],
    enableAutoDiscovery: false, // Use specific config
    ...options
  });
}

/**
 * Hook for full metadata synchronization with auto-discovery (recommended)
 */
export function useFullMetadataSync(form: UseFormReturn<any>, options?: Partial<UseMetadataSyncOptions>) {
  return useMetadataSync({
    form,
    enableAutoDiscovery: true, // Use auto-discovery for future-proofing
    ...options
  });
}

/**
 * Hook for intelligent full metadata synchronization with conflict resolution and auto-discovery
 */
export function useIntelligentFullMetadataSync(form: UseFormReturn<any>, options?: Partial<UseIntelligentMetadataSyncOptions>) {
  return useIntelligentMetadataSync({
    form,
    enableAutoDiscovery: true, // Use auto-discovery for future-proofing
    ...options
  });
} 